import { Card, CardContent, Stack, Typography } from '@mui/material';
import {
  ChartsTooltipContainer,
  mangoFusionPalette,
  PieChartProps,
  PieValueType,
} from '@mui/x-charts-pro';
import { PieChart } from '@mui/x-charts-pro/PieChart';
import { useCallback, useMemo } from 'react';
import IncDonutChartLabel from './inc-donut-chart-label/inc-donut-chart-label.component';
import IncDonutChartShareTooltip from './inc-donut-chart-share-tooltip/inc-donut-chart-share-tooltip.component';

type IncDonutChartProps = {
  data: PieValueType[];
  title: string;
  PieChartProps?: Partial<PieChartProps>;
  'data-testid'?: string;
  noDataMessage?: string;
  /**
   * When true, the center shows the largest slice's share (as a percentage)
   * with its label beneath, and each slice's tooltip adds its share below the
   * count. Defaults to false — the center shows the raw total.
   */
  showShare?: boolean;
};

const IncDonutChart = ({
  data = [],
  title,
  PieChartProps = {},
  'data-testid': dataTestId,
  noDataMessage,
  showShare = false,
}: IncDonutChartProps) => {
  const total = useMemo(
    () =>
      data.reduce((acc, item) => {
        acc += item.value;
        return acc;
      }, 0),
    [data],
  );

  // Largest slice — its share (2 dp) + label fill the donut center in share mode.
  const largestSlice = useMemo(
    () =>
      data.reduce<PieValueType | undefined>(
        (max, item) => (max && max.value >= item.value ? max : item),
        undefined,
      ),
    [data],
  );

  // In share mode, a custom tooltip stacks each slice's share below its count.
  const tooltipSlot = useCallback(
    () => (
      <ChartsTooltipContainer trigger="item">
        <IncDonutChartShareTooltip data={data} total={total} />
      </ChartsTooltipContainer>
    ),
    [data, total],
  );

  return (
    <Card>
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { paddingBottom: 2 },
        }}
      >
        <Typography variant="body2" component="div" margin={0}>
          {title}
        </Typography>
        <Stack flexGrow={1} justifyContent="center" minHeight={0}>
          <PieChart
            series={[{ data, innerRadius: '80%' }]}
            data-testid={dataTestId}
            colors={mangoFusionPalette}
            slots={showShare ? { tooltip: tooltipSlot } : undefined}
            slotProps={
              noDataMessage
                ? {
                    noDataOverlay: { message: noDataMessage },
                  }
                : undefined
            }
            {...PieChartProps}
          >
            {!PieChartProps.loading &&
              data.length > 0 &&
              (showShare && largestSlice && total > 0 ? (
                <IncDonutChartLabel sublabel={largestSlice.label as string}>
                  {`${((largestSlice.value / total) * 100).toFixed(2)}%`}
                </IncDonutChartLabel>
              ) : (
                <IncDonutChartLabel>
                  {total.toLocaleString()}
                </IncDonutChartLabel>
              ))}
          </PieChart>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default IncDonutChart;
